import { Link } from "@tanstack/react-router"
import { Textarea } from "design-system/atoms/Textarea"
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "design-system/organisms/Form"
import MessengerPageFormSelector from "modules/campaigns/components/Sections/triggers/messenger/MessengerPageFormSelector"
import { UpdateCampaignSchemaFrontend } from "modules/campaigns/hooks/useCampaignForm"
import { useFormContext } from "react-hook-form"
import { Trans, useTranslation } from "react-i18next"
import { z } from "zod"

type Props = {
  isDisabled: boolean
}

type FormValues = z.infer<typeof UpdateCampaignSchemaFrontend>
type MessengerComment = Extract<
  NonNullable<FormValues["triggerIntegration"]>,
  { type: "messengerCommentReply" }
>

export function MessengerCommentReplyForm({ isDisabled }: Props) {
  const { t } = useTranslation()
  const form = useFormContext<FormValues>()
  const triggerIntegration = form.watch("triggerIntegration")
  const messengerComment =
    triggerIntegration?.type === "messengerCommentReply" ? triggerIntegration : undefined

  // Rebuild the full variant on every edit so `type` and the account id survive partial changes.
  const buildMessengerComment = (patch: Partial<MessengerComment>): MessengerComment => ({
    type: "messengerCommentReply",
    messengerIntegrationId: messengerComment?.messengerIntegrationId ?? "",
    replies: messengerComment?.replies ?? undefined,
    allowList: messengerComment?.allowList ?? undefined,
    ...patch,
  })

  return (
    <>
      <FormField
        control={form.control}
        name="triggerIntegration"
        render={({ field }) => (
          <div>
            <FormItem className="flex flex-col gap-2">
              <FormLabel>
                {t("campaigns.trigger.metadata.messengerCommentReply.selectAccount")}
              </FormLabel>
              <FormControl>
                <MessengerPageFormSelector
                  value={messengerComment?.messengerIntegrationId}
                  emptyPlaceholder={t(
                    "campaigns.trigger.metadata.messengerCommentReply.emptyPlaceholder",
                  )}
                  placeholder={t(
                    "campaigns.trigger.metadata.messengerCommentReply.emptyPlaceholder",
                  )}
                  searchPlaceholder={t(
                    "campaigns.trigger.metadata.messengerCommentReply.searchPlaceholder",
                  )}
                  onChange={(account) => {
                    field.onChange(
                      account
                        ? buildMessengerComment({ messengerIntegrationId: account.value })
                        : null,
                      { shouldDirty: true },
                    )
                  }}
                  hasCreateNew
                  isDisabled={isDisabled}
                />
              </FormControl>
              <FormMessage />
              <span className="text-sm text-muted-foreground">
                <Trans
                  i18nKey={"campaigns.trigger.metadata.messengerCommentReply.accountDescription"}
                  components={{
                    a: (
                      <Link
                        className="underline text-muted-foreground"
                        to="/settings/channels/messenger"
                        target="_blank"
                      />
                    ),
                  }}
                />
              </span>
            </FormItem>
          </div>
        )}
      />
      <FormField
        control={form.control}
        name="triggerIntegration"
        render={({ field }) => (
          <div>
            <FormItem className="flex flex-col gap-1">
              <FormControl>
                <div className="flex flex-col gap-4">
                  <span className="text-muted-foreground text-xs block whitespace-pre-line">
                    <Trans
                      i18nKey={"campaigns.trigger.metadata.messengerCommentReply.reply.explanation"}
                      components={{
                        b: <span className="font-medium underline underline-offset-2" />,
                      }}
                    />
                  </span>
                  <div className="flex flex-col gap-1">
                    <span className="text-muted-foreground text-xs font-medium">
                      {t("campaigns.trigger.metadata.messengerCommentReply.reply.optionsTitle")}
                    </span>
                    <Textarea
                      onKeyDown={(ev) => ev.stopPropagation()}
                      disabled={isDisabled}
                      placeholder={t(
                        "campaigns.trigger.metadata.messengerCommentReply.reply.description",
                      )}
                      value={messengerComment?.replies?.join("\n") ?? ""}
                      onChange={(ev) => {
                        ev.preventDefault()
                        ev.stopPropagation()
                        field.onChange(
                          buildMessengerComment({ replies: ev.target.value.split("\n") }),
                          {
                            shouldDirty: true,
                          },
                        )
                      }}
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-muted-foreground text-xs font-medium">
                      {t("campaigns.trigger.metadata.messengerCommentReply.allowList.title")}
                    </span>
                    <Textarea
                      onKeyDown={(ev) => ev.stopPropagation()}
                      disabled={isDisabled}
                      placeholder={t(
                        "campaigns.trigger.metadata.messengerCommentReply.allowList.placeholder",
                      )}
                      value={messengerComment?.allowList?.join("\n") ?? ""}
                      onChange={(ev) => {
                        ev.preventDefault()
                        ev.stopPropagation()
                        field.onChange(
                          buildMessengerComment({ allowList: ev.target.value.split("\n") }),
                          {
                            shouldDirty: true,
                          },
                        )
                      }}
                    />
                  </div>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          </div>
        )}
      />
    </>
  )
}
